<!--
Composant basé sur un object dateConf avec le format suivant:

  {
    index: 1, // index du dateConf s'il fait parti d'un tableau de dateConfs
    date: {
      value: new Date(), // Date
      state: null,
      invalidFeedback: null
    },
    startTime: {
      value: new Date().toLocaleTimeString(), // String hh:mm:ss
      state: null,
      invalidFeedback: null
    },
    endTime: {
      value: new Date().toLocaleTimeString(), // String hh:mm:ss
      state: null,
      invalidFeedback: null
    },
    isRecurrent: false, // Boolean
    dateRecurrenceType:'DAILY', // Type de récurrence: ['DAILY', 'WEEKLY', 'MONTHLY']
    recurrenceInterval: 1, // Integer, interval entre 2 occurrences
    recurrenceMonday: false, // Boolean
    recurrenceTuesday: false, // Boolean
    recurrenceWednesday: false, // Boolean
    recurrenceThursday: false, // Boolean
    recurrenceFriday: false, // Boolean
    recurrenceSaturday: false, // Boolean
    recurrenceSunday: false, // Boolean
    dateRecurrenceMonthType: 'MONTHDAY', // Type de récurrence mensuelle: ['MONTHDAY', 'WEEKDAY_NUMBER']
    dateRecurrenceEndType: 'END_DATE', // Type de fin de récurrence: ['END_DATE', 'OCCURRENCE_NUMBER']
    recurrenceEndDate: new Date(), // Date
    recurrenceEndOccurenceNumber: 1 // Integer
  }

-->

<template>

  <b-container>

    <div class="row">

      <!-- date -->
      <div class="col-md-4 col-sm-12">
        <b-form-group
          :label="dateLabel"
          :label-for="dateId"
          :state="dateConf.date.state"
          :invalid-feedback="dateConf.date.invalidFeedback"
          class="bold-label mb-4">
          <b-form-datepicker
            :id="dateId"
            v-model="dateConf.date.value"
            :state="dateConf.date.state"
            :label-no-date-selected="$t('noDateSelected')"
            @input="handleDateInput"
            :min="minDate">
          </b-form-datepicker>
        </b-form-group>
      </div>

      <!-- startTime -->
      <div class="col-md-4 col-sm-12">
        <b-form-group
          :label="startTimeLabel"
          :label-for="startTimeId"
          :state="dateConf.startTime.state"
          :invalid-feedback="dateConf.startTime.invalidFeedback"
          class="bold-label mb-4">
          <b-form-timepicker
            :id="startTimeId"
            v-model="dateConf.startTime.value"
            :state="dateConf.startTime.state"
            @input="checkEndTimeValidity"
            :label-no-time-selected="$t('noTimeSelected')"
            :label-close-button="$t('timeCloseButton')">
          </b-form-timepicker>
        </b-form-group>
      </div>

      <!-- endTime -->
      <div v-if="withEndTime" class="col-md-4 col-sm-12">
        <b-form-group
          :label="endTimeLabel"
          :label-for="endTimeId"
          :state="dateConf.endTime.state"
          :invalid-feedback="dateConf.endTime.invalidFeedback"
          class="bold-label mb-4">
          <b-form-timepicker
            :id="endTimeId"
            v-model="dateConf.endTime.value"
            :state="dateConf.endTime.state"
            @input="checkEndTimeValidity"
            :label-no-time-selected="$t('noTimeSelected')"
            :label-close-button="$t('timeCloseButton')">
          </b-form-timepicker>
        </b-form-group>
      </div>

    </div>

    <!-- activation récurrence -->
    <b-form-checkbox
      name="isRecurrent-input"
      v-model="dateConf.isRecurrent">
      {{$t('date.recurrence.checkbox')}}
    </b-form-checkbox>

    <div class="mt-4" v-if="dateConf.isRecurrent">

      <div class="separator mb-4"/>

      <!-- interval et type de récurrence -->
      <div class="d-flex flex-row">
        <div class="bold-text d-flex align-items-center mr-2">{{$t('date.recurrence.interval')}}</div>
        <!-- interval -->
        <b-form-input
          class="number-input mr-2"
          v-model="dateConf.recurrenceInterval"
          type="number"
          :min="1"
          :step="1">
        </b-form-input>
        <!-- type de recurrence -->
        <b-form-select
          class="select"
          v-model="dateConf.dateRecurrenceType">
          <option v-for="(option) in recurrenceTypes" :value="option.value">
            {{ $t(option.text) }}
          </option>
        </b-form-select>
      </div>

      <!-- récurrence à la semaine -->
      <div class="mt-4" v-if="dateConf.dateRecurrenceType === 'WEEKLY'">
        <div class="bold-text">{{$t('date.recurrence.repeat')}}</div>
        <div class="mt-2 d-flex flex-row week-container">
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceSunday? 'active': '')" @click="updateDayActivation('Sunday')">{{$t('Sunday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceMonday? 'active': '')" @click="updateDayActivation('Monday')">{{$t('Monday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceTuesday? 'active': '')" @click="updateDayActivation('Tuesday')">{{$t('Tuesday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceWednesday? 'active': '')" @click="updateDayActivation('Wednesday')">{{$t('Wednesday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceThursday? 'active': '')" @click="updateDayActivation('Thursday')">{{$t('Thursday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceFriday? 'active': '')" @click="updateDayActivation('Friday')">{{$t('Friday')}}</div>
          <div :class="'flex-grow-1 day-button ' + (dateConf.recurrenceSaturday? 'active': '')" @click="updateDayActivation('Saturday')">{{$t('Saturday')}}</div>
        </div>
      </div>

      <!-- récurrence au mois -->
      <div class="mt-4" v-if="dateConf.dateRecurrenceType === 'MONTHLY'">
        <div class="bold-text">{{$t('date.recurrence.everyMonth')}}</div>
        <b-form-group>
          <b-form-radio class="mt-2 " v-model="dateConf.dateRecurrenceMonthType" :name="'dateRecurrenceMonthType' + dateConf.index" value="MONTHDAY">
            {{ new Date(this.dateConf.date.value).getDate() + ' ' + $t('date.recurrence.ofMonth')}}
          </b-form-radio>
          <b-form-radio class="mt-2 " v-model="dateConf.dateRecurrenceMonthType" :name="'dateRecurrenceMonthType' + dateConf.index" value="WEEKDAY_NUMBER">
            {{weekDayOfMonth() + ' ' + $t('date.recurrence.ofMonth')}}
          </b-form-radio>
        </b-form-group>
      </div>

      <!-- fin de la récurrence -->
      <div class="mt-4">
        <div class="bold-text d-flex align-items-center mr-2">{{$t('date.recurrence.end')}}</div>
        <b-form-group>
          <!-- date de fin -->
          <b-form-radio class="mt-2 radio-margin" v-model="dateConf.dateRecurrenceEndType" :name="'dateRecurrenceEndType' + dateConf.index" value="END_DATE">
            <div class="d-flex flex-row">
              <span class="d-flex align-items-center mr-2">{{$t('date.recurrence.after')}}</span>
              <b-form-datepicker
                v-model="dateConf.recurrenceEndDate"
                :min="dateConf.date.value"
                :disabled="!(dateConf.dateRecurrenceEndType === 'END_DATE')">
              </b-form-datepicker>
            </div>
          </b-form-radio>
          <!-- nb d'occurrences -->
          <b-form-radio class="mt-2 radio-margin" v-model="dateConf.dateRecurrenceEndType" :name="'dateRecurrenceEndType' + dateConf.index" value="OCCURRENCE_NUMBER">
            <div class="d-flex flex-row">
              <span class="d-flex align-items-center mr-2">{{$t('date.recurrence.after')}}</span>
              <b-form-input
                class="number-input mr-2"
                v-model="dateConf.recurrenceEndOccurenceNumber"
                type="number"
                :min="1"
                :step="1"
                :disabled="!(dateConf.dateRecurrenceEndType === 'OCCURRENCE_NUMBER')">
              </b-form-input>
              <span class="d-flex align-items-center mr-2">{{$t('date.recurrence.occurrences')}}</span>
            </div>
          </b-form-radio>
        </b-form-group>

      </div>

    </div>

  </b-container>

</template>

<script >

export default {
  name: 'DateTimeRecurrenceField',

  props: {
    dateConf: {
      type: Object,
      default: null
    },
    minDate: {
      type: Date,
      default: function() {
        return new Date()
      }
    },
    dateId: {
      type: String,
      default: 'date-input'
    },
    dateLabel: {
      type: String,
      default: function () {
        return this.$t('Date')
      }
    },
    startTimeId: {
      type: String,
      default: 'startTime-input'
    },
    startTimeLabel: {
      type: String,
      default: function () {
        return this.$t('StartTime')
      }
    },
    endTimeId: {
      type: String,
      default: 'endTime-input'
    },
    endTimeLabel: {
      type: String,
      default: function () {
        return this.$t('EndTime')
      }
    },
    withEndTime: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      recurrenceTypes: [
        { value: 'DAILY', text: 'enum.date.recurrence.type.DAILY'},
        { value: 'WEEKLY', text: 'enum.date.recurrence.type.WEEKLY'},
        { value: 'MONTHLY', text: 'enum.date.recurrence.type.MONTHLY'}
      ]
    }
  },

  computed: {

    weekDay() {
      return new Date(this.dateConf.date.value).toLocaleDateString('en-EN', { weekday: 'long' })
    }

  },

  methods: {

    // state false quand l'heure de fin est plus petite que l'heure de début
    checkEndTimeValidity() {
      if (this.dateConf.endTime.value < this.dateConf.startTime.value) {
        this.dateConf.endTime.state = false
        this.dateConf.endTime.invalidFeedback = this.$t('endTime.error')
      }
      else {
        this.dateConf.endTime.state = null
        this.dateConf.endTime.invalidFeedback = null
      }
    },

    handleDateInput() {
      this.updateRecurrenceEndDate()
      this.dateConf.recurrenceMonday = false
      this.dateConf.recurrenceTuesday = false
      this.dateConf.recurrenceWednesday = false
      this.dateConf.recurrenceThursday = false
      this.dateConf.recurrenceFriday = false
      this.dateConf.recurrenceSaturday = false
      this.dateConf.recurrenceSunday = false
      this.updateDayActivation(this.weekDay)
    },

    // mise à jour de la date de fin de récurrence pour qu'elle soit toujours supérieure ou égale à la première date sélectionnée
    updateRecurrenceEndDate() {
      if (new Date(this.dateConf.recurrenceEndDate) < new Date(this.dateConf.date.value)) {
        this.dateConf.recurrenceEndDate = this.dateConf.date.value
      }
    },

    // activation/désactivation des jours
    // impossible de désactiver le jour de la semaine de la première date sélectionnée
    updateDayActivation(weekDay) {
      switch (weekDay) {
        case 'Sunday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceSunday = ! this.dateConf.recurrenceSunday
          }
          else {
            this.dateConf.recurrenceSunday = true
          }
          break
        case 'Monday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceMonday = ! this.dateConf.recurrenceMonday
          }
          else {
            this.dateConf.recurrenceMonday = true
          }
          break
        case 'Tuesday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceTuesday = ! this.dateConf.recurrenceTuesday
          }
          else {
            this.dateConf.recurrenceTuesday = true
          }
          break
        case 'Wednesday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceWednesday = ! this.dateConf.recurrenceWednesday
          }
          else {
            this.dateConf.recurrenceWednesday = true
          }
          break
        case 'Thursday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceThursday = ! this.dateConf.recurrenceThursday
          }
          else {
            this.dateConf.recurrenceThursday = true
          }
          break
        case 'Friday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceFriday = ! this.dateConf.recurrenceFriday
          }
          else {
            this.dateConf.recurrenceFriday = true
          }
          break
        case 'Saturday':
          if (weekDay !== this.weekDay) {
            this.dateConf.recurrenceSaturday = ! this.dateConf.recurrenceSaturday
          }
          else {
            this.dateConf.recurrenceSaturday = true
          }
          break
      }
    },

    // renvoie le numéro du jour de la semaine dans le mois (ex: deuxième lundi du mois)
    weekDayOfMonth() {
      const weekDayNumberOfMonth = Math.ceil(new Date(this.dateConf.date.value).getDate() / 7)
      const dayName = new Date(this.dateConf.date.value).toLocaleDateString('fr-FR', { weekday: 'long' })
      return this.$t('date.recurrence.weekdayOfMonth.' + weekDayNumberOfMonth) + ' ' + dayName
    }

  }

}

</script>

<style scoped>

.number-input {
  width: 80px;
}

.select {
  width: 200px;
}

.week-container {
  gap: 10px;
}

.day-button {
  background-color: var(--background-grey) !important;
  border: solid 1px var(--main-border-color);
  border-radius: var(--small-border-radius);
  text-align: center;
  padding: 10px;
  cursor: pointer;
}

.day-button.active {
  background-color: var(--main-color) !important;
  border-color: var(--main-color);
  color: var(--main-color-text-color);
}

</style>



// WEBPACK FOOTER //
// src/components/b122/editFields/DateTimeRecurrenceField.vue